/* import __COLOCATED_TEMPLATE__ from './list-item.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { EntityType } from 'embercom/models/data/entity-types';
import {
  humanReadableObjectNames,
  objectIcons,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import { tracked } from '@glimmer/tracking';
import type AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import { ContentAction } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import { baseReviewReason } from 'embercom/components/knowledge-hub/content-editor/shared/utils/side-by-side-review-helper';

import type SuggestionService from 'embercom/services/suggestion-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type RouterService from '@ember/routing/router-service';
import { captureException } from 'embercom/lib/sentry';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';

interface Args {
  contentWrapper: ContentWrapper;
  activeContentId?: number;
  activeContentType?: string;
  listApi?: AiContentLibraryApi;
  borderOnHover?: boolean;
  showTopics?: boolean;
  showTitle?: boolean;
  loadSuggestions?: () => Promise<void>;
}

export default class ListItem extends Component<Args> {
  @service declare intl: IntlService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare store: $TSFixMe;
  @service declare appService: AppService;
  @service declare suggestionService: SuggestionService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare router: RouterService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @tracked isHovering = false;
  @tracked loadedDuplicateReason: string | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    if (
      this.content?.contentReviewType === ReviewType.DUPLICATE_CONTENT &&
      this.args.contentWrapper.entityId
    ) {
      this.loadDuplicateReasonData();
    }
  }

  async loadDuplicateReasonData() {
    let relationshipId = this.args.contentWrapper.entityId!;

    try {
      let relationship = (await this.store.findRecord(
        'content-service/content-section-relationship',
        relationshipId,
      )) as ContentSectionRelationship;

      await Promise.all([
        relationship.sourceSection.loadRelatedContent(ContentAction.EDIT),
        relationship.targetSection.loadRelatedContent(ContentAction.EDIT),
      ]);

      this.loadedDuplicateReason = baseReviewReason(
        relationship.sourceSection,
        relationship.targetSection,
        this.intl,
      );
    } catch (e) {
      console.warn('Failed to load duplicate reason for relationship', relationshipId, e);
    }
  }

  get content() {
    return this.args.contentWrapper.contents.firstObject;
  }

  get projectedConversationsCount() {
    return this.args.contentWrapper.contents.firstObject?.contentData.projected_conversations_count;
  }

  get projectedConversationsRanking() {
    return this.args.contentWrapper.contents.firstObject?.contentData
      .projected_conversations_ranking;
  }

  get isActive() {
    if (!this.args.activeContentId || !this.args.activeContentType) {
      return false;
    }
    return (
      Number(this.args.activeContentId) === this.contentId &&
      this.args.activeContentType === this.contentObjectName
    );
  }

  get contentId() {
    return this.args.contentWrapper.entityId!;
  }

  get contentType() {
    return this.content?.contentType;
  }

  get contentObjectName() {
    return objectNames[this.contentType!];
  }

  get numberOfPendingSuggestions() {
    return this.content?.contentData.number_of_pending_review_requests;
  }

  get reviewReason() {
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('ai-agent.suggestions.list-item.underperforming-content-reason', {
        contentTitle: this.args.contentWrapper.title,
      });
    }
    if (this.args.contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation) {
      return this.args.contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation;
    }
    if (this.content?.contentData.latest_content_review_request_reason) {
      return this.content?.contentData.latest_content_review_request_reason;
    }
    if (this.content?.contentReviewType === ReviewType.DUPLICATE_CONTENT) {
      return this.duplicateReason;
    }
    return this.intl.t('ai-agent.suggestions.list-item.review-reason-fallback');
  }

  get topic(): string {
    let suggestionMetadata = this.args.contentWrapper.contentReviewSuggestionMetadata;
    if (suggestionMetadata?.topic) {
      return suggestionMetadata?.topic;
    }
    return '';
  }

  get subtopics(): string[] {
    let suggestionMetadata = this.args.contentWrapper.contentReviewSuggestionMetadata;
    if (suggestionMetadata?.subtopics) {
      return suggestionMetadata.subtopics;
    }
    return [];
  }

  get mostRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.most_recent_content_review_request_created_at;
  }

  get leastRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.least_recent_content_review_request_created_at;
  }

  get suggestedAt() {
    return this.args.listApi?.searchParams?.sort_direction === 'asc'
      ? this.leastRecentContentReviewRequestCreatedAt
      : this.mostRecentContentReviewRequestCreatedAt;
  }

  get icon() {
    if (!this.contentType) {
      return 'article';
    }
    return objectIcons[this.contentType];
  }

  get title() {
    let reviewType = this.content?.contentReviewType;
    let contentTitle = this.args.contentWrapper.title;
    let objectKey = this.contentType === EntityType.ContentSnippet ? 'snippet' : 'article';

    let contentType: string;

    switch (reviewType) {
      case ReviewType.CREATE_CONTENT:
        contentType = 'ai-agent.suggestions.list-item.titles.add-content';
        break;
      case ReviewType.EDIT_CONTENT:
        contentType = `ai-agent.suggestions.list-item.titles.edit-${objectKey}`;
        break;
      case ReviewType.DELETE_CONTENT:
        contentType = `ai-agent.suggestions.list-item.titles.delete-${objectKey}`;
        break;
      case ReviewType.DUPLICATE_CONTENT:
        contentType = 'ai-agent.suggestions.list-item.titles.duplicate-content';
        break;
      case ReviewType.CONTRADICTING_CONTENT:
        contentType = 'ai-agent.suggestions.list-item.titles.contradicting-content';
        break;
      case ReviewType.UNDERPERFORMING_CONTENT:
        contentType = `ai-agent.suggestions.list-item.titles.underperforming-${objectKey}`;
        break;
      default:
        contentType = `ai-agent.suggestions.list-item.titles.fallback-${objectKey}`;
    }

    let title = this.intl.t(contentType);
    return this.args.showTitle ? `${title}: ${contentTitle}` : title;
  }

  get conversationsCount() {
    let conversations;
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      conversations =
        this.args.contentWrapper.contentReviewSuggestionMetadata.unresolved_conversation_ids;
    } else {
      conversations =
        this.args.contentWrapper.contentReviewSuggestionMetadata.included_conversations;
    }
    return conversations ? new Set(conversations).size : 0;
  }

  get editorMode(): EditorMode.REVIEW {
    return EditorMode.REVIEW;
  }

  get isCompleted() {
    return this.contentSuggestionsService.isSuggestionCompleted({
      entityId: this.args.contentWrapper.entityId!,
      entityType: this.args.contentWrapper.entityType,
    });
  }

  get conversationsCountTooltipText() {
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('ai-agent.suggestions.list-item.tooltip.unresolved-conversations', {
        contentTypeName: humanReadableObjectNames[this.contentType!].toLowerCase(),
      });
    }
    return this.intl.t('ai-agent.suggestions.list-item.impact-description', {
      conversation_count: this.conversationsCount,
    });
  }

  get duplicateReason(): string {
    return (
      this.loadedDuplicateReason ??
      this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason-generic',
      )
    );
  }

  get isConDupeContent() {
    return (
      this.content?.contentReviewType === ReviewType.CONTRADICTING_CONTENT ||
      this.content?.contentReviewType === ReviewType.DUPLICATE_CONTENT
    );
  }

  get canUseConDupeContentReviews() {
    return this.appService.app.canUseConDupeContentReviews;
  }

  @action
  async onSelectItem(event: Event) {
    let previousContentId = this.knowledgeHubEditorService.activeContentModel?.entityId;
    let previousEntityType = this.knowledgeHubEditorService.activeContentModel?.entityType;
    if (
      this.isActive ||
      !this.suggestionService.hasDirtyContent ||
      this.suggestionService.isOnAiInsightsPage
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.suggestionService.isChangingContent = true;

    if (this.editSnippetHasPendingReviewRequest()) {
      await this.knowledgeHubDrawerEditorService.performContentAction('cancel');
      this.navigateToItem();
      this.suggestionService.isChangingContent = false;
      return;
    }

    try {
      let confirmationResult = await this.knowledgeHubDrawerEditorService.confirmClose();

      if (confirmationResult === true) {
        await this.knowledgeHubDrawerEditorService.performContentAction('save');
        this.navigateToItem();
        if (this.suggestionService.allReviewRequestsAreActioned) {
          await this.onAllReviewRequestsActioned(previousContentId, previousEntityType);
        }
      } else if (confirmationResult.canceled) {
        await this.knowledgeHubDrawerEditorService.performContentAction('cancel');
        this.navigateToItem();
        if (this.allReviewRequestsRejected()) {
          await this.onAllReviewRequestsActioned(previousContentId, previousEntityType);
        }
      }
    } catch (error) {
      captureException(error);
    } finally {
      this.suggestionService.isChangingContent = false;
    }
  }

  private navigateToItem() {
    this.router.transitionTo({
      queryParams: {
        activeContentId: this.contentId,
        activeContentType: this.contentObjectName,
        editorMode: this.editorMode,
      },
    });
  }

  private allReviewRequestsRejected() {
    return this.suggestionService.reviewRequests.every((reviewRequest) => {
      return reviewRequest.get('status') === Status.Rejected;
    });
  }

  private editSnippetHasPendingReviewRequest(): boolean {
    return (
      this.editorMode === EditorMode.REVIEW &&
      this.knowledgeHubEditorService.activeContentIsContentSnippet &&
      this.suggestionService.reviewRequests.firstObject?.get('reviewType') ===
        ReviewType.EDIT_CONTENT &&
      !this.suggestionService.allReviewRequestsAreActioned
    );
  }

  private async onAllReviewRequestsActioned(
    previousContentId: number,
    previousEntityType: EntityType,
  ) {
    await timeout(ENV.APP._1000MS);

    this.suggestionService.contentSuggestionsService.removeSuggestionFromList({
      entity_id: previousContentId,
      entity_type: previousEntityType,
    });

    await this.suggestionService.contentSuggestionsService.fetchTopicsSummary(false);
    await this.suggestionService.contentImportService.fetchContentReviewSummary(false);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Suggestions::ListItem': typeof ListItem;
    'ai-agent/suggestions/list-item': typeof ListItem;
  }
}
